import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Container,
  Grid,
  makeStyles,
  useMediaQuery,
  useTheme,
  Backdrop,
  CircularProgress,
  Box,
} from '@material-ui/core'
import Axios from 'axios'
import { useRouter } from 'next/router'
import NewShell from './NewShell'
import CheckpriceHeroFormNew from './CheckpriceHeroFormNew'
import FilterBarNew from './FilterBarNew'
import ResultCard from '../checkprice/ResultCard'
import SearchIcon from '@material-ui/icons/Search'
import EB_Checkprice from '../externalBanner/EB_Checkprice'
import T, { specularRing, specularHost, specularRingReduced } from '../home-new/tokens'

import { useTranslation } from 'next-i18next'

const useStyles = makeStyles((theme) => ({
  // --- Hero: full-bleed banner photo with the frosted-glass tabbed search
  // card floating over it (design mockup). Same structural pattern as
  // components/home-new/HeroNew.js's banner + CheckpriceFormNew overlay.
  hero: {
    position: 'relative',
    width: '100%',
    background: T.gradient.hero,
    overflow: 'hidden',
    // Cancel NewShell's <main> top padding (92px, reserved for the fixed
    // HeaderNew) so this section's full-bleed banner photo fills up behind
    // the floating header instead of stopping at a plain strip under it —
    // same template as every other /new page hero (ServiceHero,
    // TrackingHeroNew, AboutHero, ContactHero). Mobile keeps the normal flow
    // (no fixed-header overlap concern in the stacked mobile layout).
    [theme.breakpoints.up('md')]: {
      marginTop: -92,
    },
  },
  bannerWrap: {
    position: 'relative',
    width: '100%',
    lineHeight: 0,
    [theme.breakpoints.up('md')]: {
      // Cap height so the card + banner read close to the mockup's ~1:2.5
      // banner proportions without the photo growing enormous on ultra-wide
      // screens.
      maxHeight: 620,
    },
  },
  bannerImg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    [theme.breakpoints.up('md')]: {
      maxHeight: 620,
      objectFit: 'cover',
      objectPosition: 'center 30%',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 220,
      objectFit: 'cover',
      objectPosition: 'center bottom',
    },
  },
  // Darken the lower portion of the photo slightly so the glass card (which
  // sits over the desk/laptop area) keeps AA-contrast text on busy artwork,
  // without a global scrim that would flatten the bright photo mood.
  bannerScrim: {
    position: 'absolute',
    inset: 0,
    background:
      'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.16) 100%)',
    pointerEvents: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  formArea: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      // Anchor the card top just BELOW the fixed ~92px HeaderNew. It used to be
      // vertically centered (top:50% + translateY(-50%)), but the card is nearly
      // as tall as the banner, so centering left the tab row at ~37px — behind
      // the 94px header. A fixed top clears the header at every desktop width.
      top: 110,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '86%',
      maxWidth: 940,
      zIndex: 2,
    },
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      zIndex: 2,
      width: '92%',
      maxWidth: 620,
      margin: '-40px auto 0',
    },
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  // --- Results section ---
  resultsSection: {
    // Side padding gives the results (heading + filter chips + result cards) a
    // proper gutter so they don't sit edge-to-edge — especially on mobile,
    // where the Container width == viewport (desktop still centers within its
    // maxWidth, the 24px just adds a small gutter).
    padding: '48px 24px 64px',
    [theme.breakpoints.down('sm')]: {
      padding: '36px 16px 44px',
    },
  },
  title: {
    marginTop: 0,
    marginBottom: 24,
    fontFamily: T.font.heading,
    fontWeight: 600,
    fontSize: 24,
    color: T.color.ink,
    [theme.breakpoints.down('xs')]: {
      fontSize: 19,
      marginBottom: 16,
    },
  },
  // Reskin wrapper for the shared ResultCard component (its own internals
  // stay untouched — this only overrides MUI's own global classnames it
  // renders through, e.g. MuiPaper-root, MuiChip-root, MuiButton-root — the
  // same technique CheckpriceFormNew.js uses to reskin the shared
  // Domestic/InterForm fields). The old vertical FilterCard sidebar is
  // replaced by FilterBarNew (horizontal chips, see that file) for the ?new
  // path only — results now run full-width per the design mockup.
  resultsCol: {
    fontFamily: T.font.body,
    width: '100%',
    '& .MuiPaper-root': {
      borderRadius: T.radius.lg,
      boxShadow: T.shadow.card,
      border: 'none',
      marginBottom: 16,
      padding: '20px 28px',
      overflow: 'visible',
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      [theme.breakpoints.down('xs')]: {
        padding: '16px 18px',
      },
    },
    // Per-card label / value text (rendered as plain <p> tags by
    // ResultCard — Sukhumvit font + brand ink color instead of the old
    // Kanit theme defaults).
    '& p': {
      fontFamily: T.font.body,
    },
    // Price value — brand blue, matches mockup's "ค่าบริการ" column.
    // Targets the SHIPPOP price by its explicit `data-sp-price="shippop"`
    // hook (set in ResultCard.js) rather than by DOM position. The previous
    // selector was `.MuiGrid-item:nth-last-child(2) p:last-child`, which
    // silently retargeted itself the moment a second <p> (the struck courier
    // standard price) was added to that cell: the struck price inherited
    // brand-blue + 700 on desktop while the real price lost its bold.
    // Attribute-based selection is immune to that.
    // Kept DESKTOP-ONLY (up('md')) because the old positional selector only
    // ever matched ResultCard's desktop <Grid> layout — the mobile layout
    // renders the price in plain divs and got its blue from ResultCard's own
    // `price` class at normal weight. Scoping to md+ reproduces that exactly
    // instead of newly bolding the mobile price.
    [theme.breakpoints.up('md')]: {
      '& p[data-sp-price="shippop"]': {
        color: T.color.brand,
        fontWeight: 700,
      },
    },
    // NOTE: the "แนะนำสำหรับคุณ" ribbon's pink color (mockup, vs. old red) and
    // the "ส่งพัสดุ" button label (mockup, vs. old "ใช้บริการ") are applied
    // via the optional `ribbonColor` / `actionLabel` props added to
    // ResultCard.js — both default to the exact old value, so the old
    // /checkprice page (which passes neither) is unaffected. See the
    // <ResultCard> usage below.
    // Primary CTA ("ส่งพัสดุ" — the design's pill) — solid blue
    // gradient pill with the shared "specular ring" treatment (tokens.js),
    // matching every other primary CTA on /new (submit button, track button,
    // header pill).
    '& .MuiButton-containedPrimary': {
      ...specularHost,
      background: `${T.gradient.blue} !important`,
      borderRadius: T.radius.pill,
      fontFamily: T.font.heading,
      minHeight: 44,
      overflow: 'hidden',
      touchAction: 'manipulation',
      '&:hover': {
        background: `${T.gradient.blue} !important`,
        filter: 'brightness(1.06)',
      },
      '&.Mui-disabled': {
        background: '#CCE6FF !important',
        color: '#fff !important',
      },
      '&::before': specularRing(T.ring.blue),
      '@media (prefers-reduced-motion: reduce)': {
        ...specularRingReduced,
      },
    },
    // Courier-info / "near-you" small chips — rounded pill, brand-blue text,
    // closer to the mockup's ghost button / link treatment than MUI's flat
    // gray chip default.
    '& .MuiChip-root': {
      borderRadius: T.radius.pill,
      fontFamily: T.font.body,
      color: T.color.brand,
      touchAction: 'manipulation',
    },
  },
  errorTitle: {
    textAlign: 'center',
    fontWeight: 500,
    fontSize: 40,
    marginTop: theme.spacing(10),
    fontFamily: T.font.heading,
    color: T.color.ink,
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
      marginTop: theme.spacing(5),
    },
  },
  errorSubtitle: {
    textAlign: 'center',
    fontWeight: 400,
    fontSize: 32,
    marginBottom: theme.spacing(10),
    fontFamily: T.font.body,
    color: T.color.muted,
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
      marginBottom: theme.spacing(5),
    },
  },
}))

const CheckpriceNew = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const classes = useStyles()
  const Router = useRouter()

  const { t } = useTranslation('checkprice')

  // NOTE: the `new`-survives-reroute fix lives inside CheckpriceHeroFormNew.js
  // (it wraps Domestic/InterForm in its own Context.Provider that re-appends
  // `new` to every self-re-route). Nothing needed at this level.

  const [activeTab, setActiveTab] = useState(0)
  const [checkpriceResultList, setCheckpriceResultList] = useState([])
  const [resultStatus, setResultStatus] = useState('ready')
  const [lastFetchBody, setLastFetchBody] = useState(null)
  const [selectedLlmServiceType, setSelectedLlmServiceType] = useState('MOTORCYCLE')
  // ^ ready = not show any result yet wait for user action, error = show error not found, loading = fetching, success = show results
  const [resultDeliveryType, setResultDeliveryType] = useState('domestic')
  // ^ used to keep track that the results shown is from domestic or inter search
  const [filterState, setFilterState] = useState({
    p50down: false,
    p50to100: false,
    p101to150: false,
    p150up: false,
    pickup: false,
    dropoff: false,
    d1down: false,
    d2to3: false,
    d4to5: false,
    d6to7: false,
    d8to14: false,
    d15up: false,
  })

  const [interFilterState, setInterFilterState] = useState({
    p150to500: false,
    p501to1000: false,
    p1001to2000: false,
    p2000up: false,
    pickup: false,
    dropoff: false,
    d1down: false,
    d2to3: false,
    d4to5: false,
    d6to7: false,
    d8to14: false,
    d15up: false,
  })

  // Preserve the old CheckpriceForm's deep-link behavior: /checkprice?typeid=inter&new
  // opens straight on the inter (ส่งต่างประเทศ) tab.
  useEffect(() => {
    if (!Router.isReady) return
    if (Router.query.typeid === 'inter') {
      setActiveTab(1)
    }
  }, [Router.isReady])

  const refetchWithServiceType = useCallback(async (serviceType) => {
    if (!lastFetchBody) return
    setSelectedLlmServiceType(serviceType)
    setResultStatus('loading')
    try {
      const res = await Axios.post('/api/checkprice/fetch', {
        ...lastFetchBody,
        service_type: serviceType,
      })
      setResultStatus('success')
      setHandlerCheckpriceResultList(res.data)
    } catch (err) {
      setResultStatus('error')
    }
  }, [lastFetchBody])

  // know the results is inter or domestic by check deliveryType of first result

  const setHandlerCheckpriceResultList = (data) => {
    try {
      const newData = data.map((d) => {
        return { ...d, isBanner: false }
      })

      newData.splice(2, 0, { isBanner: true })
      setCheckpriceResultList(() => newData)
    } catch (err) {
      console.error(err.message)
    }
  }

  useEffect(() => {
    if (!Array.isArray(checkpriceResultList)) return
    if (checkpriceResultList.length < 1) return
    if (!checkpriceResultList[0].deliveryType) return
    setResultDeliveryType(
      checkpriceResultList[0].deliveryType === 'inter' ? 'inter' : 'domestic'
    )
  }, [checkpriceResultList])

  const resultsFilter = (result) => {
    const {
      p50down,
      p50to100,
      p101to150,
      p150up,
      pickup,
      dropoff,
      d1down,
      d2to3,
      d4to5,
      d6to7,
      d8to14,
      d15up,
    } = filterState
    const notSelectAnyPrice = !p50down && !p50to100 && !p101to150 && !p150up
    const notSelectAnyDuration =
      !d1down && !d2to3 && !d4to5 && !d6to7 && !d8to14 && !d15up
    const notSelectPickupOrDropoff = !pickup && !dropoff

    if (!notSelectAnyPrice) {
      if (result.price < 50) {
        if (!p50down) return false
      } else if (result.price < 101) {
        if (!p50to100) return false
      } else if (result.price < 151) {
        if (!p101to150) return false
      } else if (result.price >= 151) {
        if (!p150up) return false
      } else return false
    }

    if (!notSelectPickupOrDropoff) {
      switch (result.pickupType) {
        case 'pick-up':
          if (!pickup) return false
          break
        case 'drop-off':
          if (!dropoff) return false
          break
        case 'pick-up-or-drop-off':
          break
        default:
          return false
      }
    }

    if (!notSelectAnyDuration) {
      if (!result.timeMax) return false
      if (result.timeMax === 1) {
        if (!d1down) return false
      } else if (result.timeMax <= 3) {
        if (!d2to3) return false
      } else if (result.timeMax <= 5) {
        if (!d4to5) return false
      } else if (result.timeMax <= 7) {
        if (!d6to7) return false
      } else if (result.timeMax <= 14) {
        if (!d8to14) return false
      } else if (result.timeMax > 14) {
        if (!d15up) return false
      } else return false
    }

    return true // true means this result should show, false means should hide
  }

  const interResultsFilter = (result) => {
    const {
      p150to500,
      p501to1000,
      p1001to2000,
      p2000up,
      pickup,
      dropoff,
      d1down,
      d2to3,
      d4to5,
      d6to7,
      d8to14,
      d15up,
    } = interFilterState
    const notSelectAnyPrice =
      !p150to500 && !p501to1000 && !p1001to2000 && !p2000up
    const notSelectAnyDuration =
      !d1down && !d2to3 && !d4to5 && !d6to7 && !d8to14 && !d15up
    const notSelectPickupOrDropoff = !pickup && !dropoff

    if (!notSelectAnyPrice) {
      if (result.price < 501) {
        if (!p150to500) return false
      } else if (result.price < 1001) {
        if (!p501to1000) return false
      } else if (result.price < 2001) {
        if (!p1001to2000) return false
      } else if (result.price >= 2001) {
        if (!p2000up) return false
      } else return false
    }

    if (!notSelectPickupOrDropoff) {
      switch (result.pickupType) {
        case 'pick-up':
          if (!pickup) return false
          break
        case 'drop-off':
          if (!dropoff) return false
          break
        case 'pick-up-or-drop-off':
          break
        default:
          return false
      }
    }

    if (!notSelectAnyDuration) {
      if (result.timeMax <= 1) {
        if (!d1down) return false
      } else if (result.timeMax <= 3) {
        if (!d2to3) return false
      } else if (result.timeMax <= 5) {
        if (!d4to5) return false
      } else if (result.timeMax <= 7) {
        if (!d6to7) return false
      } else if (result.timeMax <= 14) {
        if (!d8to14) return false
      } else if (result.timeMax > 14) {
        if (!d15up) return false
      } else return false
    }

    return true // true means this result should show, false means should hide
  }

  const newCheckpriceResultList = useMemo(() => {
    const newData = checkpriceResultList.map((d) => {
      return { ...d, isBanner: false }
    })

    newData.splice(2, 0, { isBanner: true })
    return newData
  }, [checkpriceResultList])

  const BANED_COURIER_CODE = ['JWDC', 'JWDF' ,'CJE', 'JNTP', 'JNTPF', 'JNTD', 'BEE', 'SCG']

  const removeBannedCourier = newCheckpriceResultList.filter((data) => {
    if (data.isBanner) {
      return true
    } else {
      return !BANED_COURIER_CODE.includes(data.courierCode)
    }
  })

  const checkpriceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'เปรียบเทียบราคาขนส่ง SHIPPOP',
    serviceType: 'Shipping rate comparison',
    areaServed: 'TH',
    provider: { '@type': 'Organization', name: 'SHIPPOP', url: 'https://www.shippop.com' },
    url: 'https://www.shippop.com/checkprice',
    description: 'เปรียบเทียบราคาค่าส่งพัสดุจากขนส่งกว่า 20+ เจ้า เช็คราคาส่งของได้ทันทีกับ SHIPPOP',
  }

  return (
    <>
      <div>
        <NewShell
          title="SHIPPOP เปรียบเทียบราคาค่าส่งพัสดุ เช็คราคา ส่งของ จองขนส่ง ทำได้เลยทันที"
          keywords="เปรียบเทียบราคา, ค่าส่งพัสดุ, เช็คค่าส่งของ, เช็คราคาส่งพัสดุ, ส่งของราคาถูก, อัตราค่าส่ง,ค่าส่ง"
          description="เปรียบเทียบราคาอัตราค่าส่งพัสดุ เช็คค่าส่ง ส่งของราคาถูก เปรียบเทียบจากหลากหลายเจ้ามากกว่า 20+ ขนส่งได้ที่นี้ที่เดียว ไม่ต้องเสียเวลาหาข้อมูลเปรียบเทียบด้วยตัวเอง เช็คราคาได้เลยทันทีกับ SHIPPOP"
          headExtra={
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(checkpriceJsonLd) }}
            />
          }
        >
          <h1 className={classes.srOnly}>{t('checkpriceForm.heroSubtitle')}</h1>
          <section className={classes.hero}>
            <div className={classes.bannerWrap}>
              <picture style={{ display: 'contents' }}>
                <source
                  srcSet="/static/img/checkprice-new/banner.webp"
                  type="image/webp"
                />
                <img
                  src="/static/img/checkprice-new/banner.png"
                  alt={t('checkpriceForm.bannerAlt')}
                  className={classes.bannerImg}
                  width={2172}
                  height={724}
                  fetchpriority="high"
                  decoding="async"
                />
              </picture>
              <div className={classes.bannerScrim} aria-hidden="true" />
            </div>
            <div className={classes.formArea}>
              {/* CheckpriceHeroFormNew wraps Domestic/InterForm in its own
                  Context.Provider that re-appends `new` (see that file) —
                  no need to duplicate the wrap here too (doing so would
                  double-append "&new&new" to the self-re-routed query). */}
              <CheckpriceHeroFormNew
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                setCheckpriceResultList={setCheckpriceResultList}
                setResultStatus={setResultStatus}
                setLastFetchBody={setLastFetchBody}
              />
            </div>
          </section>
          <Container className={classes.resultsSection}>
            {resultStatus === 'success' && (
              <>
                <h2 className={classes.title}>{t('all-couriers-list')}</h2>
                <FilterBarNew
                  setFilterState={setFilterState}
                  setInterFilterState={setInterFilterState}
                  filterState={filterState}
                  interFilterState={interFilterState}
                  deliveryType={resultDeliveryType}
                />
                <Grid
                  container
                  direction="column"
                  alignItems="center"
                  className={classes.resultsCol}
                >
                  {removeBannedCourier.map((data, index) => {
                    const { isBanner } = data
                    return (
                      <>
                        {!isBanner && (
                          <ResultCard
                            condition={data.condition}
                            timeMin={data.timeMin}
                            timeMax={data.timeMax}
                            pickupType={data.pickupType}
                            price={data.price}
                            remark={data.notice}
                            courierImg={data.courierImg}
                            courierName={data.courierName}
                            error={data.error}
                            courierCondition={data.courierCondition}
                            courierDetail={data.courierDetail}
                            hidden={
                              resultDeliveryType === 'inter'
                                ? !interResultsFilter(data)
                                : !resultsFilter(data)
                            }
                            recommended={index === 0 && !data.error}
                            key={`${data.courierName} ${data.price}`}
                            durationString={data.durationString}
                            deliveryType={data.deliveryType}
                            courierCode={data.courierCode}
                            // Origin/dest/parcel from the quote that produced this
                            // list — threaded to ResultCard so the "ส่งพัสดุ" button
                            // can hand the order off to app /shipment/create/ prefilled.
                            // Works in fetch mode too (URL query is only shallow-set).
                            shipmentHandoff={
                              lastFetchBody && lastFetchBody.data
                                ? lastFetchBody.data[0]
                                : null
                            }
                            onServiceTypeChange={refetchWithServiceType}
                            selectedLlmServiceType={selectedLlmServiceType}
                            actionLabel={t('checkpriceForm.resultAction')}
                            ribbonColor="#EB5757"
                            courierChipColor={T.color.ink}
                            // `?? null` (never undefined) opts this page into
                            // the standard-price row for EVERY card, so cards
                            // whose courier publishes no standard price still
                            // reserve the same height and the list stays even.
                            // The old /checkprice page passes nothing at all
                            // and is therefore unaffected.
                            standardPrice={data.standardPrice ?? null}
                          />
                        )}
                        {isBanner && <EB_Checkprice />}
                      </>
                    )
                  })}
                  {/* case all results have been filtered */}
                  {checkpriceResultList.filter(
                    resultDeliveryType === 'inter'
                      ? interResultsFilter
                      : resultsFilter
                  ).length == 0 && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      color="#828282"
                    >
                      <img
                        src="/static/img/not-found.svg"
                        width={isMobile ? 250 : 500}
                        alt=""
                      />
                      <h2 className={classes.errorSubtitle}>
                        {t('no-filtered-result')}
                      </h2>
                    </Box>
                  )}
                </Grid>
              </>
            )}
            {resultStatus === 'error' && (
              <>
                <h2 className={classes.errorTitle}>
                  <SearchIcon
                    style={{
                      width: isMobile ? 20 : 40,
                      height: isMobile ? 20 : 40,
                      marginRight: theme.spacing(1),
                    }}
                  />{' '}
                  {t('an-error-occured')}
                </h2>
                <h3 className={classes.errorSubtitle}>
                  {t('please-try-again')}
                </h3>
              </>
            )}
          </Container>
        </NewShell>
      </div>
      <Backdrop open={resultStatus === 'loading'} style={{ zIndex: 99999 }}>
        <CircularProgress />
      </Backdrop>
    </>
  )
}

export default CheckpriceNew
